/**
 * ConversationList - sidebar showing conversation history
 * Supports guest and authenticated users
 */
import styles from './ConversationList.module.css';
import { Button } from '@/components/ui';
import { useConversations, useArchiveConversation } from '@/api';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { useAuthStore, useModalStore } from '@/stores';
import { ROUTES } from '@/constants/routes';
import { ConfirmModal } from '@/components/modals/ConfirmModal/ConfirmModal';
import type { ConversationListItem } from '@/types/elections';

interface ConversationListProps {
    onNewChat: () => void;
    onSelectConversation: (id: number) => void;
    currentConversationId: number | null;
}

export function ConversationList({
    onNewChat,
    onSelectConversation,
    currentConversationId
}: ConversationListProps) {
    const navigate = useNavigate();
    const { isLogged } = useAuthStore();
    const { openModal } = useModalStore();
    const { data: conversations, isLoading } = useConversations();
    const archiveMutation = useArchiveConversation();

    const handleDelete = (e: React.MouseEvent, id: number) => {
        e.stopPropagation();
        openModal(
            <ConfirmModal
                title="¿Eliminar conversación?"
                message="Esta acción no se puede deshacer."
                confirmLabel="Eliminar"
                cancelLabel="Cancelar"
                onConfirm={() => archiveMutation.mutate(id)}
            />
        );
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffDays = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays === 0) return 'Hoy';
        if (diffDays === 1) return 'Ayer';
        if (diffDays < 7) return `Hace ${diffDays} días`;
        return date.toLocaleDateString('es-PE', { month: 'short', day: 'numeric' });
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <Button onClick={onNewChat} variant="primary" size="sm">
                    + Nueva Conversación
                </Button>
            </div>

            <div className={styles.list}>
                {isLoading && (
                    <div className={styles.loading}>Cargando...</div>
                )}

                {!isLogged && !conversations?.length && (
                    <div className={styles.guestNotice}>
                        <p>Tus conversaciones aparecerán aquí.</p>
                        <p className={styles.guestHint}>
                            Crea una cuenta para guardar tu historial.
                        </p>
                    </div>
                )}

                {Array.isArray(conversations) && conversations.map((conv: ConversationListItem) => (
                    <div
                        key={conv.id}
                        className={`${styles.item} ${currentConversationId === conv.id ? styles.active : ''}`}
                        onClick={() => onSelectConversation(conv.id)}
                    >
                        <div className={styles.itemContent}>
                            <span className={styles.itemTitle}>
                                {conv.title || 'Nueva conversación'}
                            </span>
                            <span className={styles.itemMeta}>
                                {formatDate(conv.last_message_at)} • {conv.message_count} mensajes
                            </span>
                        </div>
                        <button
                            className={styles.deleteButton}
                            onClick={(e) => handleDelete(e, conv.id)}
                            aria-label="Eliminar conversación"
                        >
                            ×
                        </button>
                    </div>
                ))}
            </div>

            {isLogged && (
                <div
                    className={styles.footer}
                    onClick={() => navigate(ROUTES.profile)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && navigate(ROUTES.profile)}
                >
                    <div className={styles.userIndicator}>
                        <FontAwesomeIcon icon={faUserCircle} className={styles.userIcon} />
                        <span>Mi Perfil</span>
                    </div>
                </div>
            )}
        </div>
    );
}
